import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { DeprecatedTabs } from 'ui-components/components/DEPRECATED/tabs';
import { DeprecatedTab } from 'ui-components/components/DEPRECATED/tabs/tab';
import { DeprecatedTabButton } from 'ui-components/components/DEPRECATED/tabs/tab-button';
import { DeprecatedTabContent } from 'ui-components/components/DEPRECATED/tabs/tab-content';
import { emptyArray } from 'ui-components/default-props/empty';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { Carriers } from '../../../constants/carriers';
import Global from '../../../store/global-selectors';
import { refreshPackagesSuccess } from '../../../store/seller/seller.actions';
import { SaveBar } from '../components/save-bar';
import { SettingsPageWrapper } from '../settings-page-wrapper';
import { CarrierPackages } from './carrier-packages';
import { CARRIER } from './constants';
import { CustomPackages } from './custom-packages';
import { messages, tabMessages } from './messages';
import { PackageSets } from './package-sets';
import { saveCarrierPackages } from './api';
import style from './packages-settings.module.css';
import { useMultiPackageShipments } from '../../../feature-flags/hooks/use-multi-package-shipments';
import { useQuery } from '../../../hooks/routing/use-query';

const CARRIER_PACKAGES_TAB = 'carrier';
export const CUSTOM_PACKAGES_TAB = 'custom';
export const CUSTOM_PACKAGE_SETS_TAB = 'sets';

const content = {
  carrier: CarrierPackages,
  custom: CustomPackages,
  sets: PackageSets,
};

const tabs = Object.keys(content);

export const PackagesPage = () => {
  const dispatch = useDispatch();
  const queryParams = useQuery();
  const navigate = useNavigate();
  const tabParam = queryParams.get('tab');
  const initialTab = tabs.includes(tabParam) ? tabParam : CARRIER_PACKAGES_TAB;
  const [selectedTab, setSelectedTab] = useAsyncSafeState(initialTab);
  const enableMultiPackageShipments = useMultiPackageShipments();

  const referenceCarriers = useSelector(Global.selectReferenceDatagetSellerCarriers);
  const referenceProviders = useSelector(Global.selectReferenceDatagetProviders);
  const packages = useSelector(Global.selectSellerPackages);

  const carriers = useMemo(
    () =>
      referenceCarriers.reduce((result, { carrierId }) => {
        const referenceProvider = referenceProviders.find((rp) => `${rp.carrierId}` === carrierId);
        if (referenceProvider) {
          result.push({
            carrierId,
            name: carrierId === Carriers.USPS.carrierId ? Carriers.USPS.name : referenceProvider.name,
          });
        }
        return result;
      }, []),
    [referenceCarriers, referenceProviders],
  );

  const [selectedCarrierId, setSelectedCarrierId] = useAsyncSafeState(carriers[0]?.carrierId);
  const [selectedIds, setSelectedIds] = useState(emptyArray);

  const selectedOption = useMemo(
    () => carriers.find((c) => c.carrierId === selectedCarrierId) ?? carriers[0],
    [carriers, selectedCarrierId],
  );

  const onSave = useCallback(async () => {
    if (!selectedOption) return false;
    const { carrierId } = selectedOption;
    const updatedPackages = packages.map((p) => ({
      ...p,
      ...(p.carrierId === carrierId && {
        isHidden: !selectedIds.includes(p.packageTypeId),
      }),
    }));
    await saveCarrierPackages({ carrierId, packageIds: selectedIds });
    dispatch(refreshPackagesSuccess(updatedPackages));
  }, [dispatch, packages, selectedIds, selectedOption]);

  const handleOnClickTab = useCallback(
    (tab) => {
      setSelectedTab(tab);
      navigate(`/settings/packages?tab=${tab}`, { replace: true });
    },
    [navigate, setSelectedTab],
  );

  return (
    <>
      <SettingsPageWrapper isTabbed messages={messages}>
        <DeprecatedTabs bodyClassName={style['tabs-body-override']} className={style.tabs} headerClassName={style['tabs-header-override']}>
          {tabs.map((tab) => {
            if (tab === 'sets' && !enableMultiPackageShipments) {
              return null;
            }
            const Content = content[tab];
            return (
              <DeprecatedTab key={tab} selected={selectedTab === tab}>
                <DeprecatedTabButton
                  className={style['tab-button']}
                  onClick={() => handleOnClickTab(tab)}
                  selectedClassName={style['selected-tab-button']}
                  theme='file-tab'
                >
                  <FormattedMessage {...tabMessages[tab]} />
                </DeprecatedTabButton>
                <DeprecatedTabContent className={style['tab-content']}>
                  <Content
                    carrierData={carriers}
                    selectedCarrierId={selectedCarrierId}
                    setSelectedCarrierId={setSelectedCarrierId}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                  />
                </DeprecatedTabContent>
              </DeprecatedTab>
            );
          })}
        </DeprecatedTabs>
      </SettingsPageWrapper>
      {selectedTab === CARRIER && carriers.length > 0 && <SaveBar onSave={onSave} />}
    </>
  );
};
